import { Box, Card, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { ImportJobResultCounts } from "services/KnowfullyAdminClient";

const styles = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 2,
    mb: 3,
  },
  tile: {
    p: 2,
    borderLeftWidth: 4,
    borderLeftStyle: "solid",
  },
  value: {
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.1,
  },
  label: {
    display: "block",
    mt: 0.5,
    letterSpacing: "0.08em",
    color: "text.secondary",
    fontSize: "0.68rem",
    fontWeight: 600,
  },
};

type Tile = {
  labelKey: string;
  value: number;
  color: string;
  emphasise?: boolean;
};

export default function ImportsResultTiles({
  counts,
}: Readonly<{ counts?: ImportJobResultCounts }>) {
  const { formatMessage: t } = useIntl();

  if (!counts || (counts.total ?? 0) === 0) {
    return null;
  }

  const tiles: Tile[] = [
    { labelKey: "totalLabel", value: counts.total ?? 0, color: "text.primary" },
    {
      labelKey: "processedLabel",
      value: counts.processed ?? 0,
      color: "success.main",
      emphasise: true,
    },
    {
      labelKey: "erroredLabel",
      value: counts.errored ?? 0,
      color: "error.main",
      emphasise: true,
    },
    {
      labelKey: "inProgressLabel",
      value: counts.inProgress ?? 0,
      color: "info.main",
      emphasise: true,
    },
    {
      labelKey: "pendingLabel",
      value: counts.pending ?? 0,
      color: "text.secondary",
    },
  ];

  if ((counts.cancelled ?? 0) > 0) {
    tiles.push({
      labelKey: "cancelledLabel",
      value: counts.cancelled ?? 0,
      color: "text.disabled",
    });
  }

  return (
    <Box sx={styles.grid}>
      {tiles.map((tile) => (
        <Card
          key={tile.labelKey}
          variant="outlined"
          sx={{ ...styles.tile, borderLeftColor: tile.color }}
        >
          <Typography
            variant="h5"
            sx={{
              ...styles.value,
              color: tile.emphasise ? tile.color : "text.primary",
            }}
          >
            {tile.value}
          </Typography>
          <Box component="small" sx={styles.label}>
            {t({ id: tile.labelKey })}
          </Box>
        </Card>
      ))}
    </Box>
  );
}
